import { jsPDF } from 'jspdf';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 48;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const EM_DASH = '-';

const RYG_LABELS = {
  red: 'Red',
  yellow: 'Yellow',
  green: 'Green',
};

const RYG_OPTION_TEXT = {
  risk_awareness: {
    red: "I'm blissfully unaware",
    yellow: "I think I've got it covered, but I worry I missed something",
    green: "I'm aware of the risks that relate to my financial situation",
  },
  investment_alignment: {
    red: "I'm in an employer sponsored retirement plan",
    yellow: "I'm not sure how my investment choices support and impact my financial goals",
    green: 'I have a custom-tailored investment strategy developed with my financial plan in mind',
  },
  growth_satisfaction: {
    red: "I'm disappointed with my growth, or I don't measure my return",
    yellow: 'My assets are growing... but is it enough?',
    green: "I'm getting the growth I need to fund my dreams",
  },
  investment_care: {
    red: 'I feel my investments are being neglected by myself or others',
    yellow: "I know it's important and I should be doing more",
    green: 'I am either regularly reviewing my investments, or I have a focused, diligent team that is taking great care of my investments',
  },
};

const FREQUENCY_LABELS = {
  annually: 'Annually',
  quarterly: 'Quarterly',
  monthly: 'Monthly',
  weekly: 'Weekly',
  daily: 'Daily',
};

const fmtText = (value) => {
  if (value == null) return EM_DASH;
  const text = String(value).trim();
  return text ? text : EM_DASH;
};

const fmtYesNo = (value) => {
  if (value === 'yes') return 'Yes';
  if (value === 'no') return 'No';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return EM_DASH;
};

const fmtFrequency = (value) => FREQUENCY_LABELS[String(value || '').toLowerCase()] || fmtText(value);

const fmtRyg = (sectionKey, answer) => {
  const key = String(answer || '').toLowerCase();
  if (!key) return EM_DASH;
  const color = RYG_LABELS[key] || key;
  const optionText = RYG_OPTION_TEXT[sectionKey]?.[key] || '';
  return optionText ? `${color} - ${optionText}` : color;
};

const safeFilenameStem = (name) =>
  String(name || 'client')
    .trim()
    .replace(/[^A-Za-z0-9 _.-]/g, '')
    .replace(/\s+/g, '_') || 'client';

const currentMonthYear = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = String(now.getFullYear());
  return `${mm}_${yyyy}`;
};

export const defaultCheckinPdfFilename = (clientName) =>
  `${safeFilenameStem(clientName)}_IM_Review_Form_${currentMonthYear()}.pdf`;

class PdfWriter {
  constructor(doc) {
    this.doc = doc;
    this.y = MARGIN;
  }

  ensureSpace(height) {
    if (this.y + height > PAGE_HEIGHT - MARGIN) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }

  title(text) {
    this.ensureSpace(24);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(18);
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(text, MARGIN, this.y + 14);
    this.y += 22;
  }

  subtitle(text) {
    if (!text) return;
    this.ensureSpace(14);
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
    this.doc.setTextColor(108, 117, 125);
    this.doc.text(text, MARGIN, this.y + 10);
    this.y += 14;
  }

  rule() {
    this.ensureSpace(10);
    this.doc.setDrawColor(200, 205, 210);
    this.doc.setLineWidth(0.6);
    this.doc.line(MARGIN, this.y + 2, MARGIN + CONTENT_WIDTH, this.y + 2);
    this.y += 10;
  }

  sectionHeader(number, title) {
    this.ensureSpace(24);
    this.doc.setFillColor(245, 247, 249);
    this.doc.roundedRect(MARGIN, this.y, CONTENT_WIDTH, 20, 3, 3, 'F');

    this.doc.setFillColor(38, 93, 154);
    this.doc.circle(MARGIN + 12, this.y + 10, 6, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(String(number), MARGIN + 12, this.y + 13, { align: 'center' });

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(11);
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(title, MARGIN + 24, this.y + 13);
    this.y += 24;
  }

  row(label, value) {
    const leftWidth = 245;
    const rightWidth = CONTENT_WIDTH - leftWidth;
    const leftLines = this.doc.splitTextToSize(String(label), leftWidth - 8);
    const rightLines = this.doc.splitTextToSize(String(value), rightWidth - 8);
    const lines = Math.max(leftLines.length, rightLines.length);
    const rowHeight = lines * 11 + 4;

    this.ensureSpace(rowHeight + 2);

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(80, 87, 94);
    this.doc.text(leftLines, MARGIN + 4, this.y + 9);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(rightLines, MARGIN + leftWidth + 4, this.y + 9);

    this.doc.setDrawColor(235, 238, 241);
    this.doc.setLineWidth(0.3);
    this.doc.line(MARGIN, this.y + rowHeight, MARGIN + CONTENT_WIDTH, this.y + rowHeight);

    this.y += rowHeight;
  }

  notes(text) {
    const clean = String(text || '').trim();
    if (!clean) return;
    const lines = this.doc.splitTextToSize(clean, CONTENT_WIDTH - 16);
    const height = lines.length * 11 + 16;

    this.ensureSpace(height + 8);

    this.doc.setFillColor(255, 249, 232);
    this.doc.roundedRect(MARGIN, this.y + 2, CONTENT_WIDTH, height, 3, 3, 'F');
    this.doc.setFillColor(255, 193, 7);
    this.doc.rect(MARGIN, this.y + 2, 3, height, 'F');

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.doc.setTextColor(130, 97, 0);
    this.doc.text('Notes', MARGIN + 8, this.y + 12);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(33, 37, 41);
    this.doc.text(lines, MARGIN + 8, this.y + 24);

    this.y += height + 10;
  }

  footer() {
    const pages = this.doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i += 1) {
      this.doc.setPage(i);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.doc.setTextColor(130, 138, 145);
      this.doc.text(`Page ${i} of ${pages}`, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 20, { align: 'right' });
    }
  }
}

export const buildCheckinPdfBlob = (formData = {}, { clientName = '', clientEmail = '' } = {}) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const w = new PdfWriter(doc);

  w.title('IM Review Form');
  w.subtitle([clientName, clientEmail].filter(Boolean).join(' | '));
  w.subtitle(`Generated ${new Date().toLocaleString()}`);
  w.rule();

  const contact = formData.contact || {};
  w.sectionHeader(1, 'Contact Information');
  w.row('Name', fmtText(contact.name));
  w.row('Email', fmtText(contact.email));
  w.row('What is the best phone number to reach you?', fmtText(contact.phone));
  w.row('Do you share finances with a partner or spouse?', fmtYesNo(contact.shares_finances));
  if (String(contact.shares_finances || '').toLowerCase() === 'yes') {
    w.row('Partner / Spouse Name', fmtText(contact.partner_name));
  }
  w.notes(contact.notes);

  const accounts = formData.accounts_beneficiaries || {};
  w.sectionHeader(2, 'Accounts & Beneficiaries');
  w.row('Have you added any investment account(s)?', fmtYesNo(accounts.added_investment_accounts));
  if (String(accounts.added_investment_accounts || '').toLowerCase() === 'yes') {
    w.row('What accounts did you add?', fmtText(accounts.new_accounts_details));
  }
  w.row('Have your beneficiaries changed?', fmtYesNo(accounts.beneficiaries_changed));
  if (String(accounts.beneficiaries_changed || '').toLowerCase() === 'yes') {
    w.row('What changed?', fmtText(accounts.beneficiaries_details));
  }
  w.notes(accounts.notes);

  const rygSections = [
    {
      number: 3,
      key: 'risk_awareness',
      title: 'Do you know the significant risks that could derail your finances?',
    },
    {
      number: 4,
      key: 'investment_alignment',
      title: 'How tailored are your assets and investments to your financial plan?',
    },
    {
      number: 5,
      key: 'growth_satisfaction',
      title: 'How happy are you with the growth of your assets and investments?',
    },
    {
      number: 6,
      key: 'investment_care',
      title: 'Are your assets and investments getting the respect and care they deserve?',
    },
  ];

  rygSections.forEach(({ number, key, title }) => {
    const section = formData[key] || {};
    w.sectionHeader(number, title);
    w.row('Selected answer', fmtRyg(key, section.answer));
    w.notes(section.notes);
  });

  const portfolio = formData.portfolio_monitoring || {};
  w.sectionHeader(7, 'Portfolio Monitoring');
  w.row('How often are you currently checking your portfolio?', fmtFrequency(portfolio.check_frequency));
  w.notes(portfolio.notes);

  w.footer();
  return doc.output('blob');
};

export const exportCheckinFormToPdf = (formData = {}, options = {}) => {
  const blob = buildCheckinPdfBlob(formData, options);
  const filename = defaultCheckinPdfFilename(options.clientName);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};
